import React, { useState } from "react";
import { useAppSelector } from "../../../store/hooks";
import { hasPermission, FEATURES, PERMISSIONS } from "../../../config/roleFeatures";
import StatusBadge from "./StatusBadge";
import ArrowDownUpIcon from "../../../assets/icons/ArrowDownUpIcon";
import EyeIcon from "../../../assets/icons/EyeIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import PrintIcon from "../../../assets/icons/PrintIcon";

const SalesTable = ({
  sales,
  loading,
  pagination,
  onPageChange,
  onViewInvoice,
  onPrintInvoice,
  onDeleteSale,
  onUpdateStatus,
  showProfits,
  showTotalSale,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const userRole = user?.admin_role?.toUpperCase();
  const canDelete = hasPermission(userRole, FEATURES.SALES, PERMISSIONS.DELETE);
  
  const [sortConfig, setSortConfig] = useState({
    amount: { direction: null },
    profit: { direction: null },
  });

  const handlePrevious = () => {
    if (pagination.previous) {
      onPageChange(pagination.currentPage - 1);
    }
  };

  const handleNext = () => {
    if (pagination.next) {
      onPageChange(pagination.currentPage + 1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const currentDirection = prev[key].direction;
      let newDirection = "asc";

      // 3-state cycle: null -> asc -> desc -> null
      if (currentDirection === null) {
        newDirection = "asc";
      } else if (currentDirection === "asc") {
        newDirection = "desc";
      } else if (currentDirection === "desc") {
        newDirection = null; // Reset to no sorting
      }

      // Create new config with only the clicked column active
      const newConfig = {
        amount: { direction: null },
        profit: { direction: null },
      };

      // Only set the clicked column's direction
      newConfig[key] = { direction: newDirection };

      return newConfig;
    });
  };

  const getSortedSales = () => {
    let sortedSales = [...sales];

    // Apply sorting - only one column can be sorted at a time
    if (sortConfig.amount.direction) {
      sortedSales.sort((a, b) => {
        const aAmount = parseFloat(a.amount) || 0;
        const bAmount = parseFloat(b.amount) || 0;

        if (aAmount < bAmount) {
          return sortConfig.amount.direction === "asc" ? -1 : 1;
        }
        if (aAmount > bAmount) {
          return sortConfig.amount.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else if (sortConfig.profit.direction) {
      sortedSales.sort((a, b) => {
        const aProfit = parseFloat(a.profit) || 0;
        const bProfit = parseFloat(b.profit) || 0;

        if (aProfit < bProfit) {
          return sortConfig.profit.direction === "asc" ? -1 : 1;
        }
        if (aProfit > bProfit) {
          return sortConfig.profit.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedSales;
  };
  if (loading) {
    return <div className="sales-loading">Loading sales data...</div>;
  }

  if (!sales.length) {
    return (
      <div className="sales-empty">
        <p>No sales found matching your criteria</p>
      </div>
    );
  }

  return (
    <>
      <div className="sales-table-responsive">
        <table className="sales-table">
          <thead>
            <tr>
              <th>INVOICE ID</th>
              <th>CUSTOMER</th>
              <th>DATE & TIME</th>
              <th>
                <button
                  className="sales-sort-header"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSort("amount");
                  }}
                >
                  AMOUNT{" "}
                  <ArrowDownUpIcon
                    color={sortConfig.amount.direction ? "#2F80ED" : "#57606A"}
                  />
                </button>
              </th>
              <th>
                <button
                  className="sales-sort-header"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSort("profit");
                  }}
                >
                  PROFIT{" "}
                  <ArrowDownUpIcon
                    color={sortConfig.profit.direction ? "#2F80ED" : "#57606A"}
                  />
                </button>
              </th>
              <th>CREATOR</th>
              <th>STATUS</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {getSortedSales().map((sale, index) => (
              <tr
                key={`${sale.id}-${sortConfig.amount.direction}-${sortConfig.profit.direction}-${index}`}
              >
                <td className="sales-invoice-id">{sale.invoiceId}</td>
                <td className="sales-customer-name">{sale.customerName}</td>
                <td className="sales-date-time">{sale.dateTime}</td>
                <td className="sales-amount">€{sale.amount}</td>
                <td
                  className={`sales-profit-stars ${
                    parseFloat(sale.profit) < 0 ? "negative" : ""
                  }`}
                >
                  {showProfits ? `$${sale.profit}` : "*******"}
                </td>
                <td className="sales-creator">{sale.creator}</td>

                {/* Status */}
                <td>
                  <StatusBadge status={sale.status} />
                </td>

                {/* Action Buttons */}
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn view"
                      onClick={() => onViewInvoice(sale)}
                      title="View Invoice"
                    >
                      <EyeIcon className="action-icon view" />
                    </button>

                    <button
                      className={`action-btn view ${
                        sale.status.toLowerCase() === "cancelled"
                          ? "disabled"
                          : ""
                      }`}
                      onClick={() => {
                        if (sale.status.toLowerCase() !== "cancelled") {
                          onPrintInvoice(sale);
                        }
                      }}
                      disabled={sale.status.toLowerCase() === "cancelled"}
                      title={
                        sale.status.toLowerCase() === "cancelled"
                          ? "Print not available for this status"
                          : "Print Invoice"
                      }
                    >
                      <PrintIcon
                        className={`action-icon view ${
                          sale.status.toLowerCase() === "cancelled"
                            ? "disabled"
                            : ""
                        }`}
                      />
                    </button>

                    {canDelete && (
                      <button
                        className={`action-btn delete ${
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                            ? "disabled"
                            : ""
                        }`}
                        onClick={() => {
                          if (
                            sale.status.toLowerCase() !== "cancelled" &&
                            sale.status.toLowerCase() !== "publish"
                          ) {
                            onDeleteSale(sale.id);
                          }
                        }}
                        disabled={
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                        }
                        title={
                          sale.status.toLowerCase() === "cancelled" ||
                          sale.status.toLowerCase() === "publish"
                            ? "Delete not available for this status"
                            : "Delete Sale"
                        }
                      >
                        <RedDeleteIcon
                          disabled={
                            sale.status.toLowerCase() === "cancelled" ||
                            sale.status.toLowerCase() === "publish"
                          }
                        />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {pagination.count > 25 && (
        <div className="sales-pagination-container">
          <div className="sales-pagination-info">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="sales-pagination-buttons">
            <button
              className="sales-pagination-btn"
              onClick={handlePrevious}
              disabled={!pagination.previous}
            >
              Previous
            </button>
            <button
              className="sales-pagination-btn"
              onClick={handleNext}
              disabled={!pagination.next}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesTable;
